import React, { useEffect, useState } from "react";
import {
    Loader2, Activity, Users, Send, IdCard, BadgeCheck, Percent, Clock, Briefcase, Sparkles,
    ShieldAlert, KeyRound,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import KeyRotationModal from "./KeyRotationModal";

const Card = ({ Icon, label, value, sub, tone = "foreground", testId }) => (
    <div className="card-soft p-4" data-testid={testId}>
        <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            <Icon size={11} /> {label}
        </div>
        <div className={`mt-1 font-heading font-bold text-2xl mono-score text-${tone}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
);

export default function RecruitDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showKeyRotation, setShowKeyRotation] = useState(false);

    const load = () =>
        api.get("/admin/recruit/dashboard/kpis")
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)));

    useEffect(() => {
        let mounted = true;
        const doLoad = () =>
            api.get("/admin/recruit/dashboard/kpis")
                .then((r) => mounted && setData(r.data))
                .catch((err) => toast.error(formatApiError(err)))
                .finally(() => mounted && setLoading(false));
        doLoad();
        const t = setInterval(doLoad, 60_000);
        return () => { mounted = false; clearInterval(t); };
    }, []);

    if (loading) {
        return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>;
    }
    const c = data?.cards || {};
    const recent = data?.recent_outreach || [];

    return (
        <div data-testid="recruit-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card Icon={Activity}  label="Active searches"           value={c.active_searches ?? 0}          testId="kpi-active-searches" />
                <Card Icon={Send}      label="Invitations sent"          value={c.invitations_sent ?? 0}         sub={`${c.invitations_last_7d ?? 0} in last 7d`} testId="kpi-invitations" />
                <Card Icon={IdCard}    label="Passports created (30d)"   value={c.passports_created_30d ?? 0}    testId="kpi-passports" />
                <Card Icon={BadgeCheck} label="Verified professionals"    value={c.verified_professionals ?? 0}   sub="Verifications complete" testId="kpi-verified" />
                <Card Icon={Percent}   label="Response rate"             value={`${c.response_rate_pct ?? 0}%`}  tone="emerald-600" testId="kpi-response-rate" />
                <Card Icon={Clock}     label="Avg time to first match"   value={`${c.avg_time_to_first_match_hours ?? 0}h`} testId="kpi-ttfm" />
                <Card Icon={Briefcase} label="Employer jobs (active)"    value={c.employer_jobs_active ?? 0}     testId="kpi-jobs" />
                <Card Icon={Users}     label="Team recruiters"           value={1}                                sub="Admin-only for now" testId="kpi-recruiters" />
            </div>

            {/* System health — bulk-job reaper signal. Auto-hides when there
                are zero recent reaps to keep the dashboard clean. */}
            {c.bulk_jobs_reaped_24h > 0 && (
                <div className="mt-4">
                    <div
                        className="card-soft p-4 border-amber-500/40 bg-amber-500/5"
                        data-testid="kpi-bulk-jobs-reaped"
                    >
                        <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 uppercase tracking-wider">
                            <ShieldAlert size={11} /> System health
                        </div>
                        <div className="mt-1 font-heading font-bold text-2xl mono-score text-amber-600" data-testid="kpi-bulk-jobs-reaped-value">
                            {c.bulk_jobs_reaped_24h}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            bulk job{c.bulk_jobs_reaped_24h === 1 ? "" : "s"} reaped in the last 24h — worker likely died on a backend restart. Persistent spikes need investigation.
                        </div>
                    </div>
                </div>
            )}

            {/* Secret-rotation reminder — shown whenever the oldest tracked
                secret has never been marked rotated (severity=unknown) or
                is amber/red. Auto-hides in the healthy 'ok' state. */}
            {c.key_rotation_severity && c.key_rotation_severity !== "ok" && (
                <div className="mt-4">
                    <button
                        onClick={() => setShowKeyRotation(true)}
                        className={
                            "card-soft p-4 w-full text-left transition hover:brightness-105 " +
                            (c.key_rotation_severity === "red"
                                ? "border-red-500/40 bg-red-500/5"
                                : c.key_rotation_severity === "amber"
                                ? "border-amber-500/40 bg-amber-500/5"
                                : "border-muted-foreground/30")
                        }
                        data-testid="kpi-key-rotation-tile"
                    >
                        <div className={
                            "flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider " +
                            (c.key_rotation_severity === "red" ? "text-red-600"
                                : c.key_rotation_severity === "amber" ? "text-amber-600"
                                : "text-muted-foreground")
                        }>
                            <KeyRound size={11} /> Secret rotation
                        </div>
                        <div className={
                            "mt-1 font-heading font-bold text-2xl mono-score " +
                            (c.key_rotation_severity === "red" ? "text-red-600"
                                : c.key_rotation_severity === "amber" ? "text-amber-600"
                                : "text-foreground")
                        } data-testid="kpi-key-rotation-days">
                            {c.key_rotation_oldest_days ?? "—"}
                            <span className="text-sm font-normal text-muted-foreground ml-1">days</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            since the oldest tracked API key was rotated. Click to review + mark as rotated.
                        </div>
                    </button>
                </div>
            )}

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 card-soft p-4" data-testid="recruit-recent-outreach">
                    <h3 className="font-heading font-medium text-lg mb-3">Recent activity</h3>
                    {recent.length === 0 ? (
                        <div className="py-10 text-center text-sm text-muted-foreground">
                            No outreach yet. Head to <a className="text-violet-600 font-semibold" href="/admin/recruit/job-parser">Job Parser</a> to create your first AI Hiring Brief™.
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {recent.map((r, i) => (
                                <div key={i} className="py-2 flex items-center justify-between text-sm">
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{(r.meta?.title || r.kind).slice(0, 80)}</div>
                                        <div className="text-xs text-muted-foreground">by {r.actor_email || "system"} · {r.kind.replace(/_/g, " ")}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground ml-3 shrink-0">{new Date(r.created_at).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="card-soft p-4" data-testid="recruit-quick-start">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-violet-500/10 text-violet-600 flex items-center justify-center"><Sparkles size={14} /></div>
                        <h3 className="font-heading font-medium text-base">Quick start</h3>
                    </div>
                    <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                        <li>Paste a LinkedIn / Indeed URL or a JD in the Job Parser.</li>
                        <li>AI generates an AI Hiring Brief™ with skills, salary, keywords.</li>
                        <li>Search Simonara Passports (+ external providers in Phase 2).</li>
                        <li>Invite top matches and grow the verified database.</li>
                    </ol>
                    <a href="/admin/recruit/job-parser" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:underline" data-testid="quick-start-link">
                        Start a new search →
                    </a>
                </div>
            </div>
            {showKeyRotation && (
                <KeyRotationModal
                    onClose={() => setShowKeyRotation(false)}
                    onChange={load}
                />
            )}
        </div>
    );
}
